import { Paper, Stack, Tooltip, Typography } from '@mui/material';
import { Permission } from '@procureai/permissions';
import { usePermissions } from '@procureai/permissions-ui';
import { WrapWith } from '@procureai/ui-mui';
import { chain, pipe, prop } from 'ramda';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { LineItemsQueryDataType } from '../../DemandRecommendationWidget.types';
import { Accordion } from '../Accordion/Accordion';
import { DemandRecommendationActions } from '../DemandRecommendationActions/DemandRecommendationActions';
import { I18nNamespace } from '~/common/types';

import { DemandRecommendationFormData, RecommendationType } from './DemandRecommendationForm.schema';
import { countDirty, countNonDirty, lineItemsToFormData } from './DemandRecommendationForm.util';

interface DemandRecommendationFormPropsType {
  queryData: LineItemsQueryDataType[];
  isLoading: boolean;
  isApplyAllowed: boolean;
}

export const DemandRecommendationForm = ({
  queryData,
  isLoading,
  isApplyAllowed,
}: DemandRecommendationFormPropsType) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandRecommendationWidget' });
  const form = useFormContext<DemandRecommendationFormData>();
  const { hasPermission } = usePermissions();

  const {
    watch,
    formState: { isDirty },
  } = form;

  const formRecommendations = watch();

  const recommendations = pipe<DemandRecommendationFormData['demandRequestItems'][], RecommendationType[]>(
    chain(prop('recommendations')),
  )(formRecommendations.demandRequestItems ?? []);

  const totalSelectedCount = countDirty(recommendations);
  const totalAppliedCount = countNonDirty(recommendations) === recommendations.length;

  const handleDiscardChanges = () => {
    form.reset(lineItemsToFormData(queryData));
  };

  return (
    <Stack spacing={4}>
      {!totalAppliedCount && hasPermission(Permission.DemandRecommendationApply) && (
        <Paper sx={{ p: 3 }}>
          <Stack
            direction="row"
            sx={{
              width: '100%',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="subtitle1">{t('recommendationChanges', { count: totalSelectedCount })}</Typography>
            <WrapWith component={Tooltip} condition={!isApplyAllowed} title={t(`noPermission`)} placement="bottom">
              <div>
                <DemandRecommendationActions
                  isFormDirty={isDirty}
                  isLoading={isLoading}
                  onDiscardChanges={handleDiscardChanges}
                />
              </div>
            </WrapWith>
          </Stack>
        </Paper>
      )}
      {queryData.map((demandRequestItemRecommendation, demandRequestItemIndex) => {
        return (
          <Accordion
            key={demandRequestItemRecommendation.id}
            lineItem={demandRequestItemRecommendation}
            itemIndex={demandRequestItemIndex}
          />
        );
      })}
    </Stack>
  );
};
